/**
 * Phase 2 — template-based artifact-contracts.yaml generation (--no-llm).
 */

import type { ProbeResult } from "./probe.js";

function sourcePatterns(probe: ProbeResult): string[] {
  if (probe.sourceDirs.length > 0) {
    return probe.sourceDirs.map((d) => `${d}/`);
  }
  return ["src/"];
}

function testPatterns(probe: ProbeResult): string[] {
  const patterns: string[] = [];
  for (const dir of probe.testDirs) {
    patterns.push(`${dir}/`);
  }
  patterns.push("*.test.*", "*.spec.*");
  return patterns;
}

function docPatterns(probe: ProbeResult): string[] {
  const patterns: string[] = [];
  for (const dir of probe.docDirs) {
    patterns.push(`${dir}/`);
  }
  patterns.push("*.md", "README*");
  return patterns;
}

function agentDslPatterns(probe: ProbeResult): string[] {
  const patterns: string[] = [];
  if (probe.existingConfigs.agentContracts) {
    patterns.push(probe.existingConfigs.agentContracts);
  }
  if (probe.existingConfigs.cliContract) {
    patterns.push(probe.existingConfigs.cliContract);
  }
  if (probe.existingConfigs.componentContracts.length > 0) {
    patterns.push(...probe.existingConfigs.componentContracts);
  }
  if (patterns.length === 0) {
    patterns.push("agent-contracts.yaml", "*-contract.yaml");
  }
  return patterns;
}

function yamlList(items: string[]): string {
  return items.map((item) => `      - "${item}"`).join("\n");
}

export function generateArtifactContractsYaml(probe: ProbeResult): string {
  const systemId = probe.packageJson?.name?.replace(/[^a-z0-9-]/gi, "-").toLowerCase() ?? "project";
  const systemName = probe.packageJson?.name ?? "Project Artifact Registry";

  return `artifact_contracts: "2026A"

system:
  id: ${systemId}
  name: ${systemName}

artifacts:
  codebase:
    type: source
    authority: canonical
    path_patterns:
${yamlList(sourcePatterns(probe))}

  test-code:
    type: source
    authority: canonical
    path_patterns:
${yamlList(testPatterns(probe))}

  docs:
    type: doc
    authority: canonical
    path_patterns:
${yamlList(docPatterns(probe))}

  review-reports:
    type: doc
    authority: derived
    path_patterns:
      - ".agent-logs/reviews/"

  observability-data:
    type: data
    authority: generated
    path_patterns:
      - ".agent-logs/"
      - ".observ/"

  governance-config:
    type: config
    authority: canonical
    path_patterns:
      - "project.yaml"
      - "bindings/"

  agent-dsl:
    type: config
    authority: canonical
    path_patterns:
${yamlList(agentDslPatterns(probe))}

  toolchain-config:
    type: config
    authority: canonical
    path_patterns:
      - "toolchain-contracts.yaml"

trace:
  links:
    - id: toolchain-to-lint
      from: toolchain-config
      to: codebase
      resolver: lint
      description: "Toolchain configuration drives artifact lint validation"
    - id: governance-to-observability
      from: governance-config
      to: observability-data
      resolver: observability
      description: "Governance configuration produces observability event data"
`;
}
